'use client';

import { c as _c } from "react/compiler-runtime";
import './content-feed-gallery.scss';
import React from 'react';
import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { Carousel, CarouselSlide, CarouselTrack, CarouselViewport, useCarousel } from '@sola/ark-core/molecules/carousel/carousel.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { Image } from '@sola/ark-skippy/atoms/image/image.tsx';
import { Lightbox, LightboxTrigger } from '@sola/ark-skippy/molecules/lightbox/lightbox.tsx';
import { trackSelectContent } from '@sola/tracking/lib/analytics/events.ts';
import type { ContentFeedMedia } from '../../organisms/content-feed-card/content-feed-card.tsx';

/** Returns the URL to display for a media item (thumbnail for video, direct URL for image). */
const getDisplayUrl = (media: ContentFeedMedia): string => media.type === 'video' ? media.thumbnailUrl || media.url : media.url;
interface LightboxItem {
  media: {
    image?: {
      url: string;
      alt: string;
      width: number;
      height: number;
    };
    video?: {
      url: string;
      thumbnailUrl?: string;
      width?: number;
      height?: number;
    };
  };
}
export interface ContentFeedGalleryProps {
  /** Up to 3 gallery images to display in the desktop grid */
  galleryItems: ContentFeedMedia[];
  /** All gallery items for the mobile carousel */
  allGalleryItems: ContentFeedMedia[];
  /** All image items formatted for the lightbox viewer */
  lightboxItems: LightboxItem[];
  /** Total number of photos (for "View all X photos" overlay) */
  totalPhotos: number;
  /** Whether to show "New Update" badge */
  hasNewUpdate: boolean;
  /** Fallback alt text (entry title) */
  title: string;
  /** Entry ID for tracking */
  entryId?: string;
  /** Content source type for tracking */
  sourceType?: string;
  /** i18n function */
  t: (key: string, options?: Record<string, unknown>) => string;
}

// ─── Shared Sub-Components ──────────────────────────────────────────────────

/** "New Update" badge overlay, rendered in the top-left of a gallery image. */
const NewUpdateBadge: React.FC<{
  t: ContentFeedGalleryProps['t'];
}> = t0 => {
  const $ = _c(4);
  const {
    t
  } = t0;
  let t1;
  if ($[0] !== t) {
    t1 = t("my:contentFeed.newUpdate");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="ContentFeedGallery__badge"><Badge label={t1} iconBefore="announcement-01" color="brand" /></div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};

/** Play icon overlay for video thumbnails. */
const VideoPlayOverlay: React.FC = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="ContentFeedGallery__video-play absolute top-0 left-0 flex w-full h-full items-center justify-center pointer-events-none"><ArkIcon iconName="play" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};

// ─── Carousel Indicator ─────────────────────────────────────────────────────

/** Carousel slide indicator — "2 of 7" pill overlay. */
const CarouselIndicator: React.FC<{
  totalPhotos: number;
  t: ContentFeedGalleryProps['t'];
}> = t0 => {
  const $ = _c(6);
  const {
    totalPhotos,
    t
  } = t0;
  const {
    selectedIndex
  } = useCarousel();
  const t1 = selectedIndex + 1;
  let t2;
  if ($[0] !== t || $[1] !== t1 || $[2] !== totalPhotos) {
    t2 = t("my:contentFeed.slideIndicator", {
      current: t1,
      total: totalPhotos
    });
    $[0] = t;
    $[1] = t1;
    $[2] = totalPhotos;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <div className="ContentFeedGallery__overlay absolute right-comfy-xs bottom-comfy-xs flex items-center gap-snug-lg label-potent label-md"><span>{t2}</span></div>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};

// ─── Mobile Layout ──────────────────────────────────────────────────────────

/** Mobile carousel for multiple gallery items. */
const MobileCarousel: React.FC<{
  items: ContentFeedMedia[];
  lightboxItems: LightboxItem[];
  totalPhotos: number;
  hasNewUpdate: boolean;
  title: string;
  onLightboxOpen: () => void;
  t: ContentFeedGalleryProps['t'];
}> = t0 => {
  const $ = _c(26);
  const {
    items,
    lightboxItems,
    totalPhotos,
    hasNewUpdate,
    title,
    onLightboxOpen,
    t
  } = t0;
  let t1;
  if ($[0] !== t) {
    t1 = t("my:contentFeed.openGallery");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== hasNewUpdate || $[3] !== items || $[4] !== lightboxItems || $[5] !== onLightboxOpen || $[6] !== t || $[7] !== title || $[8] !== totalPhotos) {
    let t3;
    if ($[10] !== hasNewUpdate || $[11] !== lightboxItems || $[12] !== onLightboxOpen || $[13] !== t || $[14] !== title || $[15] !== totalPhotos) {
      t3 = (m, index) => <CarouselSlide key={`carousel-${m.url}`} index={index} aria-label={`${index + 1} / ${totalPhotos}`} className="flex-none w-full"><Lightbox items={lightboxItems} startIndex={index}><LightboxTrigger className="w-full" ariaLabel={t("my:contentFeed.openGallery")} onClick={onLightboxOpen}><div className="ContentFeedGallery__image relative overflow-hidden w-full h-full">{hasNewUpdate && <NewUpdateBadge t={t} />}{m.type === "video" && <VideoPlayOverlay />}<Image image={{
                url: getDisplayUrl(m),
                alt: m.alt || title
              }} hasPlaceholder={true} aspectRatio="4-3" /></div></LightboxTrigger></Lightbox></CarouselSlide>;
      $[10] = hasNewUpdate;
      $[11] = lightboxItems;
      $[12] = onLightboxOpen;
      $[13] = t;
      $[14] = title;
      $[15] = totalPhotos;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    t2 = items.map(t3);
    $[2] = hasNewUpdate;
    $[3] = items;
    $[4] = lightboxItems;
    $[5] = onLightboxOpen;
    $[6] = t;
    $[7] = title;
    $[8] = totalPhotos;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[17] !== t2) {
    t3 = <CarouselViewport className="rounded-md"><CarouselTrack>{t2}</CarouselTrack></CarouselViewport>;
    $[17] = t2;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] !== t || $[20] !== totalPhotos) {
    t4 = totalPhotos > 1 && <CarouselIndicator totalPhotos={totalPhotos} t={t} />;
    $[19] = t;
    $[20] = totalPhotos;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  let t5;
  if ($[22] !== t1 || $[23] !== t3 || $[24] !== t4) {
    t5 = <div className="md:hidden"><Carousel aria-label={t1} alignment="start" keyboardNavigation="focusable-slides">{t3}{t4}</Carousel></div>;
    $[22] = t1;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  return t5;
};

/** Mobile single-image view (no carousel needed). */
const MobileSingle: React.FC<{
  media: ContentFeedMedia;
  lightboxItems: LightboxItem[];
  hasNewUpdate: boolean;
  title: string;
  onLightboxOpen: () => void;
  t: ContentFeedGalleryProps['t'];
}> = t0 => {
  const $ = _c(23);
  const {
    media,
    lightboxItems,
    hasNewUpdate,
    title,
    onLightboxOpen,
    t
  } = t0;
  let t1;
  if ($[0] !== t) {
    t1 = t("my:contentFeed.openGallery");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== hasNewUpdate || $[3] !== t) {
    t2 = hasNewUpdate && <NewUpdateBadge t={t} />;
    $[2] = hasNewUpdate;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== media.type) {
    t3 = media.type === "video" && <VideoPlayOverlay />;
    $[5] = media.type;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== media) {
    t4 = getDisplayUrl(media);
    $[7] = media;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = media.alt || title;
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <Image image={{
      url: t4,
      alt: t5
    }} hasPlaceholder={true} aspectRatio="4-3" />;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t2 || $[13] !== t3 || $[14] !== t6) {
    t7 = <div className="ContentFeedGallery__image relative w-full h-full rounded-md overflow-hidden">{t2}{t3}{t6}</div>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== onLightboxOpen || $[17] !== t1 || $[18] !== t7) {
    t8 = <LightboxTrigger className="w-full" ariaLabel={t1} onClick={onLightboxOpen}>{t7}</LightboxTrigger>;
    $[16] = onLightboxOpen;
    $[17] = t1;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== lightboxItems || $[21] !== t8) {
    t9 = <div className="md:hidden"><Lightbox items={lightboxItems} startIndex={0}>{t8}</Lightbox></div>;
    $[20] = lightboxItems;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
};

// ─── Desktop Layout ─────────────────────────────────────────────────────────

/** Desktop 3-image grid with lightbox and optional "View all" overlay. */
const DesktopGrid: React.FC<{
  items: ContentFeedMedia[];
  lightboxItems: LightboxItem[];
  totalPhotos: number;
  hasNewUpdate: boolean;
  title: string;
  onLightboxOpen: () => void;
  t: ContentFeedGalleryProps['t'];
}> = t0 => {
  const $ = _c(17);
  const {
    items,
    lightboxItems,
    totalPhotos,
    hasNewUpdate,
    title,
    onLightboxOpen,
    t
  } = t0;
  let t1;
  if ($[0] !== hasNewUpdate || $[1] !== items || $[2] !== lightboxItems || $[3] !== onLightboxOpen || $[4] !== t || $[5] !== title || $[6] !== totalPhotos) {
    let t2;
    if ($[8] !== hasNewUpdate || $[9] !== lightboxItems || $[10] !== onLightboxOpen || $[11] !== t || $[12] !== title || $[13] !== totalPhotos) {
      t2 = (m, index) => <div key={`gallery-${m.url}`} className="ContentFeedGallery__grid-item relative overflow-hidden"><Lightbox items={lightboxItems} startIndex={index}><LightboxTrigger className="w-full h-full" ariaLabel={t("my:contentFeed.openGallery")} onClick={onLightboxOpen}><div className="ContentFeedGallery__image relative overflow-hidden w-full h-full">{index === 0 && hasNewUpdate && <NewUpdateBadge t={t} />}{m.type === "video" && <VideoPlayOverlay />}<Image image={{
                url: getDisplayUrl(m),
                alt: m.alt || title
              }} hasPlaceholder={true} aspectRatio="4-3" /></div></LightboxTrigger></Lightbox>{index === 2 && totalPhotos > 3 && <Lightbox items={lightboxItems} startIndex={0}><LightboxTrigger className="ContentFeedGallery__overlay absolute right-comfy-xs bottom-comfy-xs flex items-center gap-snug-lg label-potent label-md" ariaLabel={t("my:contentFeed.viewAllPhotos", {
            count: totalPhotos
          })} onClick={onLightboxOpen}><span>{t("my:contentFeed.viewAllPhotos", {
                count: totalPhotos
              })}</span></LightboxTrigger></Lightbox>}</div>;
      $[8] = hasNewUpdate;
      $[9] = lightboxItems;
      $[10] = onLightboxOpen;
      $[11] = t;
      $[12] = title;
      $[13] = totalPhotos;
      $[14] = t2;
    } else {
      t2 = $[14];
    }
    t1 = items.map(t2);
    $[0] = hasNewUpdate;
    $[1] = items;
    $[2] = lightboxItems;
    $[3] = onLightboxOpen;
    $[4] = t;
    $[5] = title;
    $[6] = totalPhotos;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[15] !== t1) {
    t2 = <div className="ContentFeedGallery__grid grid overflow-hidden rounded-md gap-snug-lg">{t1}</div>;
    $[15] = t1;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  return t2;
};

/** Desktop single-image view (fewer than 3 items). */
const DesktopSingle: React.FC<{
  media: ContentFeedMedia;
  lightboxItems: LightboxItem[];
  hasNewUpdate: boolean;
  title: string;
  onLightboxOpen: () => void;
  t: ContentFeedGalleryProps['t'];
}> = t0 => {
  const $ = _c(23);
  const {
    media,
    lightboxItems,
    hasNewUpdate,
    title,
    onLightboxOpen,
    t
  } = t0;
  let t1;
  if ($[0] !== t) {
    t1 = t("my:contentFeed.openGallery");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== hasNewUpdate || $[3] !== t) {
    t2 = hasNewUpdate && <NewUpdateBadge t={t} />;
    $[2] = hasNewUpdate;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== media.type) {
    t3 = media.type === "video" && <VideoPlayOverlay />;
    $[5] = media.type;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== media) {
    t4 = getDisplayUrl(media);
    $[7] = media;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = media.alt || title;
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <Image image={{
      url: t4,
      alt: t5
    }} hasPlaceholder={true} aspectRatio="4-3" />;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t2 || $[13] !== t3 || $[14] !== t6) {
    t7 = <div className="ContentFeedGallery__image relative w-full h-full rounded-md overflow-hidden">{t2}{t3}{t6}</div>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== onLightboxOpen || $[17] !== t1 || $[18] !== t7) {
    t8 = <LightboxTrigger className="w-full" ariaLabel={t1} onClick={onLightboxOpen}>{t7}</LightboxTrigger>;
    $[16] = onLightboxOpen;
    $[17] = t1;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== lightboxItems || $[21] !== t8) {
    t9 = <Lightbox items={lightboxItems} startIndex={0}>{t8}</Lightbox>;
    $[20] = lightboxItems;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
};

// ─── Main Component ─────────────────────────────────────────────────────────

/**
 * Photo gallery with responsive layout:
 * - Mobile: swipeable single-image carousel with "X of Y" indicator
 * - Desktop (md+): 3-image grid with lightbox
 */
export const ContentFeedGallery: React.FC<ContentFeedGalleryProps> = t0 => {
  const $ = _c(23);
  const {
    galleryItems,
    allGalleryItems,
    lightboxItems,
    totalPhotos,
    hasNewUpdate,
    title,
    entryId,
    sourceType,
    t
  } = t0;
  let t1;
  if ($[0] !== entryId || $[1] !== sourceType || $[2] !== title) {
    t1 = () => {
      if (entryId) {
        const type = sourceType ? `${sourceType}:gallery` : "content-feed:gallery";
        trackSelectContent(entryId, title, type, [type]);
      }
    };
    $[0] = entryId;
    $[1] = sourceType;
    $[2] = title;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleLightboxOpen = t1;
  if (galleryItems.length === 0) {
    return null;
  }
  let t2;
  if ($[4] !== allGalleryItems || $[5] !== handleLightboxOpen || $[6] !== hasNewUpdate || $[7] !== lightboxItems || $[8] !== t || $[9] !== title || $[10] !== totalPhotos) {
    t2 = allGalleryItems.length > 1 ? <MobileCarousel items={allGalleryItems} lightboxItems={lightboxItems} totalPhotos={totalPhotos} hasNewUpdate={hasNewUpdate} title={title} onLightboxOpen={handleLightboxOpen} t={t} /> : <MobileSingle media={allGalleryItems[0]} lightboxItems={lightboxItems} hasNewUpdate={hasNewUpdate} title={title} onLightboxOpen={handleLightboxOpen} t={t} />;
    $[4] = allGalleryItems;
    $[5] = handleLightboxOpen;
    $[6] = hasNewUpdate;
    $[7] = lightboxItems;
    $[8] = t;
    $[9] = title;
    $[10] = totalPhotos;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== galleryItems || $[13] !== handleLightboxOpen || $[14] !== hasNewUpdate || $[15] !== lightboxItems || $[16] !== t || $[17] !== title || $[18] !== totalPhotos) {
    t3 = <div className="hidden md:block">{galleryItems.length >= 3 ? <DesktopGrid items={galleryItems} lightboxItems={lightboxItems} totalPhotos={totalPhotos} hasNewUpdate={hasNewUpdate} title={title} onLightboxOpen={handleLightboxOpen} t={t} /> : <DesktopSingle media={galleryItems[0]} lightboxItems={lightboxItems} hasNewUpdate={hasNewUpdate} title={title} onLightboxOpen={handleLightboxOpen} t={t} />}</div>;
    $[12] = galleryItems;
    $[13] = handleLightboxOpen;
    $[14] = hasNewUpdate;
    $[15] = lightboxItems;
    $[16] = t;
    $[17] = title;
    $[18] = totalPhotos;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  let t4;
  if ($[20] !== t2 || $[21] !== t3) {
    t4 = <div className="p-comfy-sm md:p-comfy-lg pb-comfy-xs">{t2}{t3}</div>;
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  return t4;
};